import isValid, { Network } from 'bitcoin-address-validation';
import { query as dnsQuery } from 'dns-query';
import axios from 'axios';
import type TokenizedApi from '../TokenizedApi';
import {
  checkEmail,
  makeDebouncedAsyncValidator,
  urlPath,
} from '../utils/utils';
import Service, { minutes } from './Service';
import * as schema from '../utils/schema';
import {
  restApiHandleContact,
  restApiRelationshipContact,
  TContact,
  TActivityCounterparty,
  restApiCounterpartyContact,
  TRelationshipFlag,
} from './TokenizedApiModelEntities';
import {
  protocolIdentifiers,
  defaultProtocolIdentifier,
} from '../utils/backends';

function isBsvAddress(input?: string) {
  return !!input && !!isValid(input, Network.mainnet);
}

interface TPaymailCapabilities {
  bsvalias: string;
  capabilities: {
    pki: string; // "https://api.example.com/paymail/pki/{alias}@{domain.tld}"
    paymentDestination: string; // "https://api.example.com/paymail/destination/{alias}@{domain.tld}"
    f12f968c92d6: string; // "https://api.example.com/paymail/profile/{alias}@{domain.tld}"
    '6745385c3fc0'?: boolean; // true (indicates messages must be signed)
    f636191c8fe6?: string; // "https://api.example.com/paymail/negotiation-capabilities/{alias}@{domain.tld}"
  };
}

interface TPaymailPki {
  bsvalias: string; // "1.0",
  handle: string; //"hank.rearden@example.com",
  pubkey: string; //"123abc..."
}

interface TPaymailProfile {
  name: string; // "Hank Rearden",
  avatar: string; // "https://api.example.com/avatars/hank.rearden"
  result?: {
    // Centbee incorrectly returns the info here
    name: string;
    avatar: string;
  };
}

interface TPaymailTokenizedCapabilities {
  protocols: string[];
  options: {
    send_disabled?: boolean;
    auto_send_response?: boolean;
    receive?: boolean;
    four_step_exchange?: boolean;
    auto_exchange_response?: boolean;
  };
}

/** @category Relationships */
export interface TDisplayContact extends TContact {
  isValid?: boolean;
  isUnverifiedPaymail?: boolean;
  isUnsupportedPaymail?: boolean;
  isAnonymous?: boolean;
  isInternal?: boolean;
  isVerified?: boolean;
  initials?: string | null;
  address?: string;

  // Tokenized Protocol capabilities
  supportsInstrumentSend?: boolean;
  autoRespondsToSendRequest?: boolean;
  supportsInstrumentRequest?: boolean;
  supportsInstrumentTrade?: boolean;
  autoRespondsToTradeRequest?: boolean;
}

/** @category SDK Modules */
export default class Relationships extends Service {
  protocolIdentifier: string;

  /** @internal */
  constructor(tokenizedApi: TokenizedApi) {
    super(tokenizedApi);

    this.protocolIdentifier =
      protocolIdentifiers[tokenizedApi.config.tokenizedBackend] ||
      defaultProtocolIdentifier;
  }

  contactInitialsFromName(name = '', locale?: string) {
    return name
      .split(' ')
      .filter((word) => word?.length)
      .slice(0, 2)
      .map((component) => [...component][0])
      .join('')
      .toLocaleUpperCase(locale);
  }

  async getPaymailInfo(paymail: string): Promise<TDisplayContact | null> {
    const parts = checkEmail(paymail);
    if (!parts) {
      return null;
    }

    let target = parts.domain;

    let hostDiscovery;
    try {
      hostDiscovery = await dnsQuery(
        {
          question: { type: 'SRV', name: `_bsvalias._tcp.${parts.domain}` },
        },
        {
          endpoints: ['dns.google', 'dns.cloudflare.com'],
          timeout: 5000, // Short timeout – we don’t want to pause the app for slow DNS
          retries: 0, // No retrying
        },
      );
    } catch (error) {
      // Paymail host DNS query failure
      console.error('Paymail DNS lookup error', error);
      return {
        displayHandle: paymail,
        isUnverifiedPaymail: true,
      };
    }

    if (hostDiscovery?.answers) {
      for (const answer of hostDiscovery.answers) {
        if (answer?.type === 'SRV' && answer?.data?.target) {
          target = answer.data.target;
          break;
        }
      }
    }

    let capabilityDiscovery: TPaymailCapabilities;
    try {
      const url = new URL('https://host/.well-known/bsvalias');
      url.hostname = target;
      if (url.hostname === 'host') {
        throw new Error('Invalid paymail domain');
      }
      capabilityDiscovery = (
        await axios.get<TPaymailCapabilities>(url.toString())
      ).data;
    } catch (error) {
      // Host did not respond to paymail capability query
      return null;
    }

    if (!capabilityDiscovery?.capabilities?.pki) {
      // Missing PKI endpoint
      console.error(`No paymail PKI endpoint for ${parts.domain}`);
      return null;
    }
    const pkiEndpoint = capabilityDiscovery.capabilities.pki
      .replaceAll('{alias}', encodeURIComponent(parts.localPart))
      .replaceAll('{domain.tld}', encodeURIComponent(parts.domain));
    let handle;
    try {
      const pki = (await axios.get<TPaymailPki>(pkiEndpoint)).data;
      handle = pki.handle;
    } catch (error) {
      console.error(`Paymail not found ${paymail}`, error);
      return null;
    }
    if (!checkEmail(handle)) {
      console.error(
        `Paymail check returned invalid handle for ${parts.domain}`,
      );
      return null;
    }

    // Tokenized does not support paymails that require signed messages
    const isUnsupportedPaymail =
      !!capabilityDiscovery.capabilities['6745385c3fc0'];

    let paymailInfo: TDisplayContact = {
      displayHandle: handle,
      isUnsupportedPaymail,
    };

    if (capabilityDiscovery.capabilities.f12f968c92d6) {
      const profileEndpoint = capabilityDiscovery.capabilities.f12f968c92d6
        .replaceAll('{alias}', encodeURIComponent(parts.localPart))
        .replaceAll('{domain.tld}', encodeURIComponent(parts.domain));
      let profile;
      try {
        profile = (await axios.get<TPaymailProfile>(profileEndpoint)).data;
      } catch (error) {
        console.error(`Paymail profile query failed for ${paymail}`, error);
      }
      if (profile) {
        const profileName = profile.name || profile.result?.name;
        const profileAvatar = profile.avatar || profile.result?.avatar;
        paymailInfo = {
          ...paymailInfo,
          displayName: profileName === handle ? undefined : profileName,
          avatarUrl: profileAvatar,
        };
      }
    }

    if (capabilityDiscovery.capabilities.f636191c8fe6) {
      const tokenizedCapabilitiesEndpoint =
        capabilityDiscovery.capabilities.f636191c8fe6
          .replaceAll('{alias}', encodeURIComponent(parts.localPart))
          .replaceAll('{domain.tld}', encodeURIComponent(parts.domain));
      let tokenizedCapabilities;
      try {
        tokenizedCapabilities = (
          await axios.get<TPaymailTokenizedCapabilities>(
            tokenizedCapabilitiesEndpoint,
          )
        ).data;
      } catch (error) {
        console.error(
          `Paymail Tokenized capabilities query failed for ${paymail}`,
          error,
        );
      }
      if (
        tokenizedCapabilities?.protocols?.includes?.(this.protocolIdentifier)
      ) {
        paymailInfo = {
          ...paymailInfo,
          supportsInstrumentSend: !tokenizedCapabilities.options?.send_disabled,
          autoRespondsToSendRequest:
            !!tokenizedCapabilities.options?.auto_send_response,
          supportsInstrumentRequest: !!tokenizedCapabilities.options?.receive,
          supportsInstrumentTrade:
            !!tokenizedCapabilities.options?.four_step_exchange,
          autoRespondsToTradeRequest:
            !!tokenizedCapabilities.options?.auto_exchange_response,
        };
      }
    }

    return paymailInfo;
  }

  handleQuery = this.makeQuery(
    (
      query,
      {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        handle,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        counterparty,
      }: { handle?: string; counterparty?: TActivityCounterparty | null } = {},
    ) => ({
      ...query,
      staleTime: minutes(10),
      // Even when logged out, we can make public
      // paymail queries to get handle info (name and avatar)
      enabled: true,
    }),
    async (
      context,
      {
        handle,
        counterparty,
      }: { handle?: string; counterparty?: TActivityCounterparty | null } = {},
    ): Promise<TDisplayContact> => {
      const intl = this.getIntl();

      let isAnonymous;
      let isInternal;
      let isVerified;
      let name;
      let address;
      let isValid = true;
      if (counterparty) {
        handle = counterparty.displayHandle;
        isAnonymous = !!counterparty.isAnonymous;
        if (!isAnonymous) {
          // Sometimes the API will set displayName="Blockchain"
          // for anonymous counterparties, which we want to replace
          // with internationalized text below
          name = counterparty.displayName;
        }
        isInternal = !!counterparty.isInternal;
        isVerified = !!counterparty.isVerified;

        // Special case for new account BSV funding transfer
        if (handle === 'teller@' && !name) {
          name = intl.formatMessage({
            defaultMessage: 'Tokenized Pty Ltd',
            description: 'Sender name for new account BSV funding transfers',
            id: 'TokenizedSDK:contact.name.funding',
          });
        }
      } else {
        // Identify handle format (TODO: the API should
        // probably do this for us)
        const handleParts = checkEmail(handle);
        if (handleParts) {
          isAnonymous = false;
          isInternal =
            handleParts.domain === this.tokenizedApi.account.userHandleDomain;
        } else if (isBsvAddress(handle)) {
          address = handle;
          isAnonymous = true;
          isInternal = false;
        } else {
          // Not a valid handle
          isAnonymous = true;
          isInternal = false;
          isValid = false;
        }
      }

      if (isAnonymous || !handle) {
        let displayName = '';
        let initials = null;
        if (name) {
          displayName = name;
          initials = this.contactInitialsFromName(name, this.getLocale());
        } else if (isValid && (isAnonymous || address)) {
          displayName = intl.formatMessage({
            defaultMessage: 'A BSV address',
            description: 'Anonymous recipient/sender display name',
            id: 'TokenizedSDK:contact.anonymous.name',
          });
          initials = '₿';
        }
        return {
          isValid,
          address,
          displayName,
          isAnonymous,
          isInternal,
          isVerified: !!isVerified,
          initials,
          supportsInstrumentSend: isInternal,
          autoRespondsToSendRequest: isInternal,
          supportsInstrumentRequest: isInternal,
          supportsInstrumentTrade: isInternal,
          autoRespondsToTradeRequest: isInternal,
        };
      }

      if (!isInternal) {
        if (isValid && !isAnonymous) {
          const paymailInfo = await this.getPaymailInfo(handle);
          if (paymailInfo) {
            return {
              isValid,
              isUnverifiedPaymail: paymailInfo.isUnverifiedPaymail,
              isUnsupportedPaymail: paymailInfo.isUnsupportedPaymail,
              displayHandle: paymailInfo.displayHandle,
              displayName: paymailInfo.displayName || paymailInfo.displayHandle,
              isAnonymous,
              isInternal,
              isVerified: true,
              avatarUrl: paymailInfo.avatarUrl,
              initials: paymailInfo.displayName
                ? this.contactInitialsFromName(
                    paymailInfo.displayName,
                    this.getLocale(),
                  )
                : null,
              supportsInstrumentSend: paymailInfo.supportsInstrumentSend,
              autoRespondsToSendRequest: paymailInfo.autoRespondsToSendRequest,
              supportsInstrumentRequest: paymailInfo.supportsInstrumentRequest,
              supportsInstrumentTrade: paymailInfo.supportsInstrumentTrade,
              autoRespondsToTradeRequest:
                paymailInfo.autoRespondsToTradeRequest,
            };
          } else {
            isValid = false;
          }
        }

        let formattedName = name;
        if (!name && handle) {
          formattedName = handle;
        }
        return {
          isValid,
          displayHandle: handle,
          displayName: formattedName,
          isAnonymous,
          isInternal,
          isVerified: !!isVerified,
          initials: name
            ? this.contactInitialsFromName(name, this.getLocale())
            : null,
          supportsInstrumentSend: isInternal,
          autoRespondsToSendRequest: isInternal,
          supportsInstrumentRequest: isInternal,
          supportsInstrumentTrade: isInternal,
          autoRespondsToTradeRequest: isInternal,
        };
      }

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('handles', 'contact', handle),
        {
          validateStatus: (status) => status < 300 || status == 404,
        },
        null,
      );

      if (!response || response.status === 404) {
        // Note: this is what happens when querying an account
        // that has a handle set up, but no root key yet
        return {
          isValid: false,
          displayHandle: handle,
          displayName: name || handle,
          isAnonymous: false,
          isInternal: false,
          isVerified: false,
          initials: name
            ? this.contactInitialsFromName(name, this.getLocale())
            : null,
        };
      }

      const { normalizedValue } = schema.normalizeResponse(
        'Query handle',
        response?.data,
        restApiHandleContact,
      );

      return {
        ...normalizedValue,
        isValid,
        isAnonymous,
        isInternal,
        isVerified: !!isVerified,
        initials: this.contactInitialsFromName(
          normalizedValue?.displayName,
          this.getLocale(),
        ),
        supportsInstrumentSend: isInternal,
        autoRespondsToSendRequest: isInternal,
        supportsInstrumentRequest: isInternal,
        supportsInstrumentTrade: isInternal,
        autoRespondsToTradeRequest: isInternal,
      };
    },
  );

  contactToDisplayContact(contact: TContact): TDisplayContact {
    const result: TDisplayContact = { ...contact };

    const handleParts = checkEmail(result.displayHandle);
    if (handleParts) {
      result.isValid = true;
      result.isAnonymous = false;
      result.isInternal =
        handleParts.domain === this.tokenizedApi.account.userHandleDomain;
    } else {
      result.isValid = false;
      result.isAnonymous = true;
      result.isInternal = false;
    }

    if (result.displayName) {
      result.initials = this.contactInitialsFromName(
        result.displayName,
        this.getLocale(),
      );
    } else {
      result.initials = null;
    }

    if (!result.displayName && result.displayHandle) {
      result.displayName = result.displayHandle;
    }

    if (result.isInternal) {
      result.supportsInstrumentSend = true;
      result.autoRespondsToSendRequest = true;
      result.supportsInstrumentRequest = true;
      result.supportsInstrumentTrade = true;
      result.autoRespondsToTradeRequest = true;
    }

    return result;
  }

  relationshipsQuery = this.makeQuery(
    (query, profileId?: string) => ({
      ...query,
      staleTime: minutes(10),
      enabled: query.enabled && !!profileId,
    }),
    async (
      context,
      profileId?: string,
    ): Promise<TDisplayContact[] | null | undefined> => {
      if (!profileId) {
        return null;
      }

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'contacts'),
      );

      const { normalizedValue } = schema.normalizeResponse(
        'Query contacts',
        response?.data?.data,
        schema.arrayOf(restApiRelationshipContact),
      );

      if (normalizedValue?.length) {
        // Eliminate duplicates
        const contacts = new Map<string, TDisplayContact>(
          normalizedValue.map((contact) => [
            contact.displayHandle || '',
            this.contactToDisplayContact(contact),
          ]),
        );
        return [...contacts.values()].sort((a, b) =>
          (a.displayName || '').localeCompare(b.displayName || ''),
        );
      }

      return normalizedValue;
    },
  );

  async addRelationship(profileId: string, handle: string) {
    return this.editRelationship(profileId, handle, ['contact']);
  }

  async deleteRelationship(profileId: string, handle: string) {
    return this.editRelationship(profileId, handle, ['hidden']);
  }

  async editRelationship(
    profileId: string,
    handle: string,
    flags: TRelationshipFlag[],
  ) {
    const response = await this.tokenizedApi.endpoints.post(
      urlPath('profiles', profileId, 'contacts'),
      {
        handle,
        flags,
      },
    );

    const { normalizedValue } = schema.normalizeResponse(
      'Edit relationship',
      response?.data,
      restApiRelationshipContact,
    );

    this.relationshipsQuery.invalidate(profileId);
    return normalizedValue;
  }

  makeDebouncedHandleValidator(
    {
      allowAnyPaymail,
      allowOrganizations,
      validateForTransferType,
    }: {
      allowAnyPaymail?: boolean;
      allowOrganizations?: boolean;
      validateForTransferType?: 'sendBsv' | 'send' | 'request' | 'trade';
    } = {
      allowOrganizations: true,
    },
  ) {
    return makeDebouncedAsyncValidator(async (handle?: string) => {
      const intl = this.getIntl();

      const contact = await this.handleQuery
        .fetch({ handle })
        .catch((): TDisplayContact => ({ isValid: false }));

      if (
        !contact?.isValid ||
        (contact?.isAnonymous && validateForTransferType !== 'sendBsv')
      ) {
        if (validateForTransferType === 'sendBsv') {
          return intl.formatMessage({
            defaultMessage: 'Must be a valid paymail handle or BSV address',
            description: 'Handle validation failure',
            id: 'TokenizedSDK:handle.validation.address',
          });
        } else {
          return intl.formatMessage({
            defaultMessage: 'Must be a valid paymail handle',
            description: 'Handle validation failure',
            id: 'TokenizedSDK:handle.validation.paymail',
          });
        }
      }
      if (!allowAnyPaymail && !contact?.isInternal) {
        return intl.formatMessage({
          defaultMessage: 'Must be a Tokenized paymail handle',
          description: 'Handle validation failure',
          id: 'TokenizedSDK:handle.validation.internal',
        });
      }
      if (!allowOrganizations && contact?.entityType !== 'I') {
        return intl.formatMessage({
          defaultMessage: 'Must be an individual, not an organization',
          description: 'Handle validation failure',
          id: 'TokenizedSDK:handle.validation.individual',
        });
      }
      if (contact?.isUnsupportedPaymail) {
        return intl.formatMessage({
          defaultMessage: 'This paymail host is not supported',
          description: 'Handle validation failure (unsupported paymail)',
          id: 'TokenizedSDK:handle.validation.unsupported',
        });
      }
      // Note: if the paymail could not be verified, then let the transfer go
      // ahead (just display warning) – the API will check and return an error if necessary
      if (validateForTransferType && !contact?.isUnverifiedPaymail) {
        switch (validateForTransferType) {
          case 'sendBsv':
            // BSV can be sent to any valid address (already checked above)
            break;
          case 'send':
            if (!contact?.supportsInstrumentSend) {
              return intl.formatMessage({
                defaultMessage: 'This paymail does not support Tokenized sends',
                description: 'Handle validation failure',
                id: 'TokenizedSDK:handle.validation.tokenized.send',
              });
            }
            break;
          case 'request':
            if (!contact?.supportsInstrumentRequest) {
              return intl.formatMessage({
                defaultMessage:
                  'This paymail does not support Tokenized requests',
                description: 'Handle validation failure',
                id: 'TokenizedSDK:handle.validation.tokenized.request',
              });
            }
            break;
          case 'trade':
            if (!contact?.supportsInstrumentTrade) {
              return intl.formatMessage({
                defaultMessage:
                  'This paymail does not support Tokenized trades',
                description: 'Handle validation failure',
                id: 'TokenizedSDK:handle.validation.tokenized.trade',
              });
            }
            break;
          default:
            throw new Error(
              `Unrecognized transfer type ${validateForTransferType}`,
            );
        }
      }
    }, 500);
  }
}
